import { deleteCookie } from '@/util/cookies';
import { getImpersonatingCookieOptions } from '@/util/impersonation';

/** Non-httpOnly so the banner can show demo copy without a server round-trip. */
export const DEMO_ORG_ACCESS_COOKIE = 'demo-org-access';

/** Shared by enter-route zod, signed session payload, and UI. */
export const DEMO_ORG_ROLES = ['org_admin', 'site_admin', 'teacher', 'student'] as const;
export type DemoOrgRole = (typeof DEMO_ORG_ROLES)[number];

/** Reuse users.magicschool-role.* keys — DemoOrgRole uses `_`, locale ids use `-`. */
export const roleLabelId = (role: DemoOrgRole) => `users.magicschool-role.${role.replace('_', '-')}` as const;

export const DEMO_ORG_MODES = ['configure', 'demo'] as const;
export type DemoOrgMode = (typeof DEMO_ORG_MODES)[number];

/** Amplitude `access_role`; site_admin/student omit configure (see CONFIGURE_ROLES / CONFIGURE_MODE_ROLES). */
export const DEMO_ORG_ACCESS_ROLES = [
  'teacher_persistent',
  'org_admin_persistent',
  'teacher_temporary',
  'org_admin_temporary',
  'student_temporary',
  'site_admin_temporary',
] as const;
export type DemoOrgAccessRole = (typeof DEMO_ORG_ACCESS_ROLES)[number];

/** Amplitude `access_role` shared by enter (mode) and exit (isTempUser) so they can't drift. */
export const demoOrgAccessRole = (role: DemoOrgRole, isTemporary: boolean): DemoOrgAccessRole =>
  `${role}_${isTemporary ? 'temporary' : 'persistent'}` as DemoOrgAccessRole;

export type DemoOrgAccessCookieValue = {
  orgName: string;
  role: DemoOrgRole;
  mode: DemoOrgMode;
};

/** Match impersonating UI cookie options — both are non-httpOnly banner cookies. */
export function getDemoOrgAccessCookieOptions() {
  return getImpersonatingCookieOptions();
}

/** Clear demo-org-access with server-matching attributes (Chrome requires an exact match). */
export function clearDemoOrgAccessCookie(): void {
  const { path, secure, sameSite } = getDemoOrgAccessCookieOptions();
  deleteCookie(DEMO_ORG_ACCESS_COOKIE, path, { secure, sameSite });
}

export function encodeDemoOrgAccessCookieValue(value: DemoOrgAccessCookieValue): string {
  return encodeURIComponent(JSON.stringify(value));
}

export function decodeDemoOrgAccessCookieValue(raw: string | null | undefined): DemoOrgAccessCookieValue | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    const value = parsed as Record<string, unknown>;
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof value.orgName === 'string' &&
      DEMO_ORG_ROLES.includes(value.role as DemoOrgRole) &&
      DEMO_ORG_MODES.includes(value.mode as DemoOrgMode)
    ) {
      return parsed as DemoOrgAccessCookieValue;
    }
    return null;
  } catch {
    return null;
  }
}
